import { store } from "../app/store";
import { login, logout } from "../features/userSlice";
import { update } from "../features/roleSlice";
import { fetchSession } from "../views/Admin/Access/accessApi";

// Keeps the signed-in user's roles, branch, and Page Access grants in step
// with the server, so a change made in the User Manager or on Page Access
// reaches people without them signing out. One poller per page load
// (NavListAuth remounts on every route change, so it can't own the timer):
// every POLL_MS while the tab is visible, straight away when the tab comes
// back into focus, and on demand via requestSessionRefresh() after an admin
// saves a change.
//
// The server's answer is authoritative and applied verbatim — a stripped
// role or a missing record fails closed. Only an unreachable server keeps
// what we had, since there was no answer to trust. A deactivated account is
// signed out (InitUser then explains why).
//
// Each poll also reports the page this tab is on (Currently Online, the
// mobile voice assistant) and picks up an admin's pending "send them to a
// page", which RemoteRedirect (components/Navigation) follows.

const POLL_MS = 20 * 1000;
const MIN_GAP_MS = 4 * 1000;
const REFRESH_EVENT = "node:session-refresh";
export const REDIRECT_EVENT = "node:remote-redirect";

let started = false;
let inFlight = false;
let queued = false;
let lastRun = 0;
let synced = false;

// False until the first answer (or failure) this page load. The route guard
// waits on it so a page someone was granted doesn't flash "no access" while
// their grants load.
export const isSessionSynced = () => synced;

export const requestSessionRefresh = () => {
  window.dispatchEvent(new Event(REFRESH_EVENT));
};

// After a navigation: report the new page soon, without forcing past the
// MIN_GAP_MS throttle (the next poll catches anything skipped).
export const reportRoute = () => {
  syncSession();
};

const sameList = (a = [], b = []) =>
  a.length === b.length && a.every((item, i) => item === b[i]);

async function syncSession({ force = false } = {}) {
  const now = Date.now();
  if (inFlight) {
    // An admin's save mid-poll must not be lost behind the older answer.
    if (force) queued = true;
    return;
  }
  if (!force && now - lastRun < MIN_GAP_MS) return;
  const realUser = store.getState().user.user;
  if (!realUser?.id) return;

  inFlight = true;
  lastRun = now;
  try {
    const session = await fetchSession(realUser.id, window.location.pathname);
    // Signed out or switched accounts while the request was out.
    const current = store.getState().user.user;
    if (current?.id !== realUser.id) return;

    if (session.deactivated) {
      store.dispatch(logout());
      return;
    }

    const roles = session.roles || [];
    const grantedPaths = session.grantedPaths || [];
    const slice = store.getState().role.role || {};
    if (
      !synced ||
      !sameList(slice.roles, roles) ||
      !sameList(slice.grantedPaths, grantedPaths)
    ) {
      store.dispatch(update({ roles, grantedPaths, syncedAt: now }));
    }

    // The user record carries roles and branch too (profile, forms, and
    // anything reading user.branch), so keep it in step.
    const branch = session.branch || current.branch;
    if (!sameList(current.roles, roles) || current.branch !== branch) {
      store.dispatch(login({ ...current, roles, branch }));
    }

    if (session.redirect?.path) {
      window.dispatchEvent(
        new CustomEvent(REDIRECT_EVENT, { detail: session.redirect })
      );
    }
  } catch (err) {
    // Unreachable server: keep what we have and try again next tick.
  } finally {
    inFlight = false;
    if (!synced) {
      synced = true;
      // Re-render the route guard even when nothing changed.
      store.dispatch(update({ syncedAt: Date.now() }));
    }
    if (queued) {
      queued = false;
      syncSession({ force: true });
    }
  }
}

export function startLiveSession() {
  if (started) return;
  started = true;

  const onVisible = () => {
    if (document.visibilityState === "visible") syncSession();
  };
  setInterval(onVisible, POLL_MS);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
  window.addEventListener(REFRESH_EVENT, () => syncSession({ force: true }));

  syncSession({ force: true });
}
